import { ElementRef } from '@angular/core';
import { getWidthBreakpointScreen, windowSizeObserver } from '@local/ts-infra';
import { Subject } from 'rxjs';
import { DrawerOptions, DEFAULT_DRAWER_CONFIG } from './drawer-options';

export type DrawerTarget = 'route_change' | 'size_change' | 'manual_close' | 'drawer_switch';

export interface DrawerCloseData {
  via?: DrawerTarget;
}

export class DrawerRef<TComponent, TData> {
  private keyboardListener?: (event: KeyboardEvent) => void;

  element?: ElementRef;
  compInstance: TComponent;

  readonly destroy$ = new Subject<TData>();
  readonly close$ = new Subject<DrawerCloseData>();
  readonly update$ = new Subject<TData>();

  private destroyed = false;
  private closed = false;

  constructor(
    private _data: TData,
    private _options?: DrawerOptions
  ) {
    windowSizeObserver(100).subscribe(() => this.calcPreviewState());
    this.configureClosingBehavior();
  }

  get data(): TData {
    return this._data;
  }

  get options(): DrawerOptions {
    return this._options ?? DEFAULT_DRAWER_CONFIG;
  }

  update(data: TData): void {
    this._data = data;
    this.update$.next(data);
  }

  close(via?: DrawerTarget): void {
    this.close$.next({ via });
    this.close$.complete();
    this.closed = true;
    this.destroy();
  }

  private destroy(): void {
    if (this.destroyed) {
      return;
    }
    if (!this.closed) {
      this.close$.complete();
    }
    this.update$.complete();
    this.destroy$.complete();
    this.destroyed = true;
    this.removeKeyboardListener();
  }

  calcPreviewState() {
    const closeOnScreenSizes = this._options?.responsive?.closeOnScreenSizes ?? DEFAULT_DRAWER_CONFIG.responsive.closeOnScreenSizes;
    if (closeOnScreenSizes.includes(getWidthBreakpointScreen())) {
      this.close('size_change');
    }
  }

  private configureClosingBehavior() {
    this.removeKeyboardListener();

    const mode = this._options?.layout?.mode;
    if (mode === 'side-drawer') {
      this.keyboardListener = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          this.escape();
        }
      };
      document.addEventListener('keydown', this.keyboardListener);
    }
  }

  private removeKeyboardListener() {
    if (this.keyboardListener) {
      document.removeEventListener('keydown', this.keyboardListener);
      this.keyboardListener = undefined;
    }
  }

  private escape() {
    this.close('manual_close');
    this.removeKeyboardListener();
  }
}
